import {
  EMPTY_HELD_INPUT,
  parseNormalizedHeldInput,
  parseNormalizedLifecycleEvent,
  type NormalizedHeldInput,
  type PauseReason,
} from "./lifecycle/events";
import { cloneAndFreezePlainData } from "./lifecycle/kernel";

export const FIXED_TICK_MS = 50 as const;
export const FIXED_TICK_HZ = 20 as const;
export const MAX_CATCH_UP_TICKS = 4 as const;

export function millisecondsToTicksCeil(milliseconds: number): number {
  if (!Number.isFinite(milliseconds) || milliseconds < 0) {
    throw new RangeError("milliseconds must be finite and non-negative");
  }

  return Math.ceil(milliseconds / FIXED_TICK_MS);
}

export function ticksToMilliseconds(ticks: number): number {
  if (!Number.isSafeInteger(ticks) || ticks < 0) {
    throw new RangeError("ticks must be a non-negative safe integer");
  }

  return ticks * FIXED_TICK_MS;
}

export interface FixedTickContext {
  readonly tick: number;
  readonly tickMs: typeof FIXED_TICK_MS;
  readonly heldInput: Readonly<NormalizedHeldInput>;
}

export interface OverloadSample {
  readonly elapsedMs: number;
  readonly processedTicks: number;
  readonly droppedMs: number;
}

export interface FrameAdvanceResult {
  readonly ticksRun: number;
  readonly overload?: Readonly<OverloadSample>;
}

export interface FixedStepDriverOptions<Snapshot> {
  readonly initialSnapshot: Snapshot;
  readonly monotonicOriginMs: number;
  readonly tick: (
    snapshot: Readonly<Snapshot>,
    context: Readonly<FixedTickContext>,
  ) => Snapshot | Readonly<Snapshot>;
  readonly onOverload?: (sample: Readonly<OverloadSample>) => void;
}

function assertMonotonicTime(value: unknown, label: string): asserts value is number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    throw new RangeError(`${label} must be finite and non-negative`);
  }
}

export class FixedStepDriver<Snapshot> {
  readonly #tick: FixedStepDriverOptions<Snapshot>["tick"];
  readonly #onOverload: FixedStepDriverOptions<Snapshot>["onOverload"];
  #previousSnapshot: Readonly<Snapshot>;
  #currentSnapshot: Readonly<Snapshot>;
  #lastMonotonicMs: number;
  #accumulatorMs = 0;
  #authorityTick = 0;
  #paused = false;
  #pauseReason: PauseReason | null = null;
  #heldInput: Readonly<NormalizedHeldInput> = EMPTY_HELD_INPUT;
  #lifecycleGeneration = 0;

  constructor(options: FixedStepDriverOptions<Snapshot>) {
    assertMonotonicTime(options.monotonicOriginMs, "monotonic origin");
    this.#tick = options.tick;
    this.#onOverload = options.onOverload;
    this.#currentSnapshot = cloneAndFreezePlainData(options.initialSnapshot);
    this.#previousSnapshot = cloneAndFreezePlainData(options.initialSnapshot);
    this.#lastMonotonicMs = options.monotonicOriginMs;
  }

  get previousSnapshot(): Readonly<Snapshot> {
    return this.#previousSnapshot;
  }

  get currentSnapshot(): Readonly<Snapshot> {
    return this.#currentSnapshot;
  }

  get authorityTick(): number {
    return this.#authorityTick;
  }

  get accumulatorMs(): number {
    return this.#accumulatorMs;
  }

  get interpolationAlpha(): number {
    return Math.min(1, Math.max(0, this.#accumulatorMs / FIXED_TICK_MS));
  }

  get isPaused(): boolean {
    return this.#paused;
  }

  get pauseReason(): PauseReason | null {
    return this.#pauseReason;
  }

  get heldInput(): Readonly<NormalizedHeldInput> {
    return this.#heldInput;
  }

  submitHeldInput(input: unknown): boolean {
    const normalized = parseNormalizedHeldInput(input);
    if (this.#paused) {
      return false;
    }
    this.#heldInput = normalized;
    return true;
  }

  handleLifecycle(event: unknown): void {
    const normalized = parseNormalizedLifecycleEvent(event);
    this.#lifecycleGeneration += 1;
    if (normalized.type === "pause") {
      this.#paused = true;
      this.#pauseReason = normalized.reason;
      this.#accumulatorMs = 0;
      this.#lastMonotonicMs = 0;
      this.#heldInput = EMPTY_HELD_INPUT;
      return;
    }

    this.#paused = false;
    this.#pauseReason = null;
    this.#accumulatorMs = 0;
    this.#lastMonotonicMs = normalized.monotonicOriginMs;
    this.#heldInput = EMPTY_HELD_INPUT;
  }

  advanceFrame(monotonicNowMs: unknown): Readonly<FrameAdvanceResult> {
    assertMonotonicTime(monotonicNowMs, "monotonic frame time");
    if (this.#paused) {
      return Object.freeze({ ticksRun: 0 });
    }

    const previousOrigin = this.#lastMonotonicMs;
    if (monotonicNowMs < previousOrigin) {
      throw new RangeError("monotonic frame time cannot move backward");
    }

    const elapsedMs = monotonicNowMs - previousOrigin;
    const accumulatedMs = this.#accumulatorMs + elapsedMs;
    const availableTicks = Math.floor(accumulatedMs / FIXED_TICK_MS);
    const plannedTicks = Math.min(availableTicks, MAX_CATCH_UP_TICKS);
    const droppedTicks = Math.max(0, availableTicks - plannedTicks);
    this.#lastMonotonicMs = monotonicNowMs;
    this.#accumulatorMs = accumulatedMs - availableTicks * FIXED_TICK_MS;

    let ticksRun = 0;
    const frameLifecycleGeneration = this.#lifecycleGeneration;
    for (let index = 0; index < plannedTicks; index += 1) {
      if (this.#paused || this.#lifecycleGeneration !== frameLifecycleGeneration) {
        break;
      }
      const nextTick = this.#authorityTick + 1;
      const nextSnapshot = this.#tick(
        this.#currentSnapshot,
        Object.freeze({ tick: nextTick, tickMs: FIXED_TICK_MS, heldInput: this.#heldInput }),
      );
      this.#previousSnapshot = this.#currentSnapshot;
      this.#currentSnapshot = cloneAndFreezePlainData(nextSnapshot);
      this.#authorityTick = nextTick;
      ticksRun += 1;
    }

    if (droppedTicks === 0) {
      return Object.freeze({ ticksRun });
    }

    const overload = Object.freeze({
      elapsedMs,
      processedTicks: ticksRun,
      droppedMs: droppedTicks * FIXED_TICK_MS,
    });
    this.#onOverload?.(overload);
    return Object.freeze({ ticksRun, overload });
  }
}
